import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { API_CONFIG } from '@/config/api';
import { MAGIC_LINK_CONFIG, validateRedirectPath } from '@/config/auth';
import { getPortalAuth, setPortalAuth } from '@/core/auth/storage';
import { validatePortalToken } from '@/core/auth/tokenUtils';
import { resolvePostLoginTarget } from '@/core/auth/portalSurface';

const API_URL = API_CONFIG.URL;

type AuthStatus = 'checking' | 'exchanging' | 'success' | 'error';

/**
 * Legacy client-portal sub-routes that live BELOW the approval shell at
 * ``/client/:clientId``. A redirect matching one of these gets the
 * ``/client/<clientId>`` prefix prepended.
 *
 * Any other validated redirect (e.g. ``/creator/<clientId>?briefId=...``
 * or ``/editor/<clientId>``) points to an independent top-level app and
 * is navigated to verbatim.
 */
const CLIENT_SHELL_SUBROUTES = [
  '/planning',
  '/ideas',
  '/report',
  '/social-media',
] as const;

function isClientShellSubroute(path: string): boolean {
  return CLIENT_SHELL_SUBROUTES.some(
    (entry) => path === entry || path.startsWith(`${entry}/`) || path.startsWith(`${entry}?`)
  );
}

function buildRedirectTarget(
  clientId: string,
  validatedRedirect: string | null,
  role: string
): string {
  // Legacy sub-routes keep the client-shell prefix; everything then goes
  // through the shared role/workspace guard (DD-026) so a session never
  // lands on a surface that doesn't match its role.
  const expanded =
    validatedRedirect && isClientShellSubroute(validatedRedirect)
      ? `/client/${clientId}${validatedRedirect}`
      : validatedRedirect;
  return resolvePostLoginTarget(expanded, clientId, role);
}

interface ErrorInfo {
  code: string;
  message: string;
}

export const MagicLinkAuth: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [error, setError] = useState<ErrorInfo | null>(null);

  const retryHref = useMemo(() => {
    const redirectParam = searchParams.get('redirect');
    const validatedRedirect = validateRedirectPath(redirectParam);
    // One login for every portal role (DD-026).
    return validatedRedirect
      ? `/portal/login?redirect=${encodeURIComponent(validatedRedirect)}`
      : '/portal/login';
  }, [searchParams]);

  useEffect(() => {
    const handleAuth = async () => {
      if (!clientId) {
        setStatus('error');
        setError({
          code: 'missing_client',
          message: 'Link non valido - cliente non specificato',
        });
        return;
      }

      // Check for existing valid session first
      const existingAuth = getPortalAuth(clientId);
      if (existingAuth) {
        const validation = validatePortalToken(existingAuth.token, clientId);
        if (validation.valid) {
          // Already authenticated - redirect directly
          setStatus('success');
          const redirectParam = searchParams.get('redirect');
          const validatedRedirect = validateRedirectPath(redirectParam);

          setTimeout(() => {
            const targetPath = buildRedirectTarget(
              clientId,
              validatedRedirect,
              existingAuth.role || 'client'
            );
            navigate(targetPath, { replace: true });
          }, MAGIC_LINK_CONFIG.redirectDelay);
          return;
        }
      }

      // No valid session - need to exchange the magic link
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setError({
          code: 'missing_token',
          message: 'Token di autenticazione non fornito',
        });
        return;
      }

      setStatus('exchanging');

      try {
        const response = await fetch(`${API_URL}/portal/auth/exchange-magic-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await response.json();

        if (data.success) {
          // Store tokens using shared storage utility
          // access_token + refresh_token from new flow, session_token for backward compat
          setPortalAuth(clientId, {
            token: data.accessToken || data.sessionToken,
            refreshToken: data.refreshToken,
            role: data.role,
            clientName: data.clientName,
          });

          setStatus('success');

          // Handle deep linking - redirect to specified path or dashboard
          const redirectParam = searchParams.get('redirect');
          const validatedRedirect = validateRedirectPath(redirectParam);

          setTimeout(() => {
            const targetPath = buildRedirectTarget(
              clientId,
              validatedRedirect,
              data.role || 'client'
            );
            navigate(targetPath, { replace: true });
          }, MAGIC_LINK_CONFIG.redirectDelay);
        } else {
          setStatus('error');
          setError({
            code: data.error || 'unknown_error',
            message: data.message || 'Autenticazione fallita',
          });
        }
      } catch (err) {
        console.error('Magic link exchange error:', err);
        setStatus('error');
        setError({
          code: 'network_error',
          message: 'Errore di connessione. Riprova.',
        });
      }
    };

    handleAuth();
  }, [clientId, searchParams, navigate]);

  if (status === 'checking' || status === 'exchanging') {
    return (
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-cyan-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="flex items-center justify-center mb-4">
            <svg className="animate-spin h-12 w-12 text-blue-600" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Autenticazione...</h1>
          <p className="text-gray-600">Stiamo verificando il tuo accesso</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-linear-to-br from-blue-50 to-cyan-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accesso Consentito!</h1>
          <p className="text-gray-600">Reindirizzamento al portale...</p>
        </div>
      </div>
    );
  }

  // Error state
  const getErrorDisplay = () => {
    switch (error?.code) {
      case 'magic_link_used':
        return {
          icon: (
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          ),
          bgColor: 'bg-amber-100',
          title: 'Link Gia Utilizzato',
          description: 'Questo link di accesso e stato gia utilizzato.',
        };
      case 'magic_link_expired':
        return {
          icon: (
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          bgColor: 'bg-amber-100',
          title: 'Link Scaduto',
          description: 'Questo link di accesso e scaduto dopo 24 ore.',
        };
      case 'magic_link_revoked':
        return {
          icon: (
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          ),
          bgColor: 'bg-red-100',
          title: 'Accesso Revocato',
          description: 'Questo link di accesso e stato revocato.',
        };
      default:
        return {
          icon: (
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          bgColor: 'bg-red-100',
          title: 'Accesso Negato',
          description: error?.message || 'Si e verificato un errore durante l\'autenticazione.',
        };
    }
  };

  const errorDisplay = getErrorDisplay();

  return (
    <div className="min-h-screen bg-linear-to-br from-blue-50 to-cyan-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className={`w-16 h-16 ${errorDisplay.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
          {errorDisplay.icon}
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2 text-center">{errorDisplay.title}</h1>
        <p className="text-gray-600 mb-6 text-center">{errorDisplay.description}</p>
        <Link
          to={retryHref}
          replace
          className="block w-full text-center px-4 py-3 bg-blue-600 text-white rounded-md font-medium hover:bg-blue-700 transition-colors"
        >
          Richiedi un nuovo link
        </Link>
      </div>
    </div>
  );
};

export default MagicLinkAuth;
